import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import usePageTitle from "../hooks/usePageTitle";
import { ArrowLeft, MapPin, Store, CreditCard, Star } from "lucide-react";

const LOCATIONS = {
  "costa-del-sol": {
    id: "costa_del_sol",
    name: "Costa del Sol",
    title: "Discount Card Costa del Sol",
    description: "Get your Advanta Plus discount card for Costa del Sol. Save up to 20% at restaurants, shops, spas and experiences in Marbella, Malaga, Puerto Banus, Estepona, Fuengirola, Benalmadena and Torremolinos.",
    metaDesc: "Advanta Plus discount card for Costa del Sol — save at 100+ restaurants, shops and services in Marbella, Malaga, Puerto Banus and more. From €14.99.",
    image: "https://images.unsplash.com/photo-1613429561582-2b20fc0028dd?w=1920",
    areas: ["Marbella", "Malaga", "Puerto Banus", "Estepona", "Fuengirola", "Benalmadena", "Torremolinos"],
    highlights: [
      "100+ partner restaurants, bars and beach clubs",
      "Luxury spas and wellness centres",
      "Shopping discounts along the Golden Mile",
      "Experience the best of Andalusia for less",
    ],
    seoText: "Costa del Sol is one of Spain's most popular holiday destinations, stretching along the southern coast of Andalusia. With Advanta Plus, visitors and residents can enjoy exclusive discounts at the best restaurants in Marbella, beach clubs in Puerto Banus, tapas bars in Malaga, and shops across the entire coast. Our digital discount card works instantly — simply show your QR code before paying and save up to 20% at participating businesses. Whether you're dining at a beachfront chiringuito in Torremolinos, shopping in Estepona's old town, or enjoying a spa day in Fuengirola, your Advanta Plus card unlocks savings everywhere.",
  },
  "tenerife": {
    id: "tenerife",
    name: "Tenerife",
    title: "Discount Card Tenerife",
    description: "Get your Advanta Plus discount card for Tenerife. Save up to 20% at restaurants, shops, excursions and experiences in Los Cristianos, Costa Adeje, Playa de las Americas, Santa Cruz and Puerto de la Cruz.",
    metaDesc: "Advanta Plus discount card for Tenerife — save at restaurants, shops and experiences in Los Cristianos, Costa Adeje, Playa de las Americas and more. From €14.99.",
    image: "https://images.unsplash.com/photo-1613333046289-4cd8e6a6270e?w=1920&q=80",
    areas: ["Los Cristianos", "Costa Adeje", "Playa de las Americas", "Santa Cruz", "Puerto de la Cruz", "Los Gigantes"],
    highlights: [
      "Popular restaurants and bars across the south coast",
      "Excursion and activity discounts",
      "Shopping deals in local boutiques",
      "Save on dining from tapas to fine cuisine",
    ],
    seoText: "Tenerife is the largest of the Canary Islands and a year-round holiday destination for millions of visitors. With Advanta Plus, you can save money at the best restaurants in Los Cristianos, beach bars in Costa Adeje, shopping in Playa de las Americas, and experiences across the island. Our discount card app gives you instant access to exclusive deals — just show your digital card before requesting the bill. From casual dining in Puerto de la Cruz to sunset drinks in Los Gigantes, Advanta Plus helps you enjoy more of Tenerife for less.",
  },
  "majorca": {
    id: "majorca",
    name: "Mallorca",
    title: "Discount Card Mallorca",
    description: "Get your Advanta Plus discount card for Mallorca. Save up to 20% at restaurants, shops, beach clubs and experiences in Palma, Magaluf, Alcudia, Pollensa, Santa Ponsa and Soller.",
    metaDesc: "Advanta Plus discount card for Mallorca — save at restaurants, shops, beach clubs and services in Palma, Magaluf, Alcudia and more. From €14.99.",
    image: "https://images.unsplash.com/photo-1559202987-59d8a2827511?w=1920&q=80",
    areas: ["Palma", "Magaluf", "Alcudia", "Pollensa", "Santa Ponsa", "Soller", "Porto Cristo"],
    highlights: [
      "Beach clubs and restaurants across the island",
      "Old town dining in Palma and Pollensa",
      "Shopping discounts in Palma's boutiques",
      "Save on boat trips, excursions and experiences",
    ],
    seoText: "Mallorca is the jewel of the Balearic Islands, offering stunning beaches, vibrant nightlife and world-class dining. With Advanta Plus, visitors and residents can unlock exclusive discounts at restaurants in Palma's old town, beach clubs in Magaluf, seafood restaurants in Alcudia, and boutique shops across the island. Our digital discount card works instantly on your phone — no physical card needed. Whether you're exploring the charming streets of Soller, relaxing on the beaches of Santa Ponsa, or enjoying fine dining in Pollensa, your Advanta Plus card saves you money at every turn.",
  },
};

const LocationPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = LOCATIONS[slug];

  usePageTitle(
    location ? `${location.title} — Advanta Plus` : "Location Not Found",
    location ? location.metaDesc : ""
  );

  if (!location) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center text-white">
        <div className="text-center">
          <h1 className="text-3xl mb-4">Location not found</h1>
          <Link to="/" className="text-[#D4AF37]">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Hero */}
      <div className="relative h-[50vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${location.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/70 via-[#020617]/50 to-[#020617]" />
        <div className="relative z-10 text-center px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl text-white mb-4">
            {location.title}
          </h1>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            {location.description}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-16">
        {/* Price cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-16">
          {[
            { name: "1 Week", price: "14.99" },
            { name: "2 Weeks", price: "18.99" },
            { name: "1 Month", price: "24.99" },
          ].map((plan) => (
            <div key={plan.name} className="bg-[#0B0F19] rounded-xl p-6 border border-[#D4AF37]/10 text-center">
              <p className="text-white/50 text-sm mb-2">{plan.name}</p>
              <p className="text-3xl font-bold text-[#D4AF37]">€{plan.price}</p>
              <Button
                onClick={() => navigate("/membership")}
                className="btn-gold mt-4 w-full"
              >
                Get Card
              </Button>
            </div>
          ))}
        </div>

        {/* Areas covered */}
        <div className="mb-16">
          <h2 className="font-['Playfair_Display'] text-2xl text-white mb-6 flex items-center gap-3">
            <MapPin className="w-6 h-6 text-[#D4AF37]" />
            Areas Covered in {location.name}
          </h2>
          <div className="flex flex-wrap gap-3">
            {location.areas.map((area) => (
              <span key={area} className="px-4 py-2 bg-[#0B0F19] rounded-full border border-[#D4AF37]/20 text-white/80 text-sm">
                {area}
              </span>
            ))}
          </div>
        </div>

        {/* Highlights */}
        <div className="mb-16">
          <h2 className="font-['Playfair_Display'] text-2xl text-white mb-6 flex items-center gap-3">
            <Star className="w-6 h-6 text-[#D4AF37]" />
            What You Get
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {location.highlights.map((h, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#0B0F19] rounded-xl p-5 border border-[#D4AF37]/10">
                <Store className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <p className="text-white/80">{h}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-[#D4AF37]/10 to-[#B8860B]/10 rounded-2xl p-12 border border-[#D4AF37]/20 mb-16">
          <h2 className="font-['Playfair_Display'] text-3xl text-white mb-4">
            Start Saving in {location.name} Today
          </h2>
          <p className="text-white/60 mb-8 max-w-lg mx-auto">
            Get your digital discount card from just €14.99 and save at restaurants, shops and experiences across {location.name}.
          </p>
          <Button
            onClick={() => {
              localStorage.setItem("advanta_territory", location.id);
              navigate("/membership");
            }}
            className="btn-gold px-10 py-6 text-lg"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            Get Your {location.name} Discount Card
          </Button>
        </div>

        {/* SEO text */}
        <div className="prose prose-invert max-w-none">
          <p className="text-white/40 text-sm leading-relaxed">
            {location.seoText}
          </p>
        </div>

        {/* View partners link */}
        <div className="text-center mt-12">
          <Link
            to="/businesses"
            onClick={() => localStorage.setItem("advanta_territory", location.id)}
            className="text-[#D4AF37] hover:underline"
          >
            View all partner businesses in {location.name} →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LocationPage;
